import { useMemo } from 'react';
import { useLiveRxQuery } from 'rxdb/plugins/react';
import { useStableQuery } from '../../db/useStableQuery';
import {
    computeTimeEntryWarnings,
    computeTimeEntryWarningDetails,
    type TimeEntryDocType,
    type TimeEntryWarningInput,
    type TimeEntryWarningCode
} from '@ast-data/shared';

/**
 * ArbZG break/rest warnings for every time entry the device can see, keyed by
 * id. Reads the whole local collection, so it spans ALL projects and days — a
 * short rest between two workdays flags both entries.
 */
export function useTimeEntryWarnings(): Map<string, TimeEntryWarningCode[]> {
    const entriesQuery = useStableQuery<TimeEntryDocType>({});
    const { results: entriesDocs } = useLiveRxQuery<TimeEntryDocType>({
        collection: 'timeEntries',
        query: entriesQuery
    });
    const entries = useMemo(
        () => entriesDocs.map((doc) => doc.toMutableJSON() as TimeEntryDocType),
        [entriesDocs]
    );
    return useMemo(() => computeTimeEntryWarnings(entries), [entries]);
}

/**
 * The ArbZG warnings the entries WOULD carry if the form were saved as it
 * stands. A form may hold SEVERAL drafts at once (one Mitarbeiter each), so the
 * codes are the UNION over all of them — they share the day and the times.
 *
 * Each draft replaces its saved self (matched by id) or joins as a new entry,
 * so the day's totals and the rest to neighbouring days come out as they will
 * after saving. None of these rules is about one row: a break is too short
 * relative to the WHOLE day, across every project.
 *
 * Pass an empty list while the form has nothing comparable yet.
 */
export function useTimeEntryDraftWarnings(
    drafts: readonly TimeEntryWarningInput[]
): TimeEntryWarningCode[] {
    const entriesQuery = useStableQuery<TimeEntryDocType>({});
    const { results: entriesDocs } = useLiveRxQuery<TimeEntryDocType>({
        collection: 'timeEntries',
        query: entriesQuery
    });
    const entries = useMemo(
        () => entriesDocs.map((doc) => doc.toMutableJSON() as TimeEntryDocType),
        [entriesDocs]
    );
    return useMemo(() => {
        if (drafts.length === 0) {
            return [];
        }
        const draftIds = new Set(drafts.map((draft) => draft.id));
        const others = entries.filter((entry) => !draftIds.has(entry.id));
        const warnings = computeTimeEntryWarnings([...others, ...drafts]);
        const codes = new Set<TimeEntryWarningCode>();
        for (const draft of drafts) {
            for (const code of warnings.get(draft.id) ?? []) {
                codes.add(code);
            }
        }
        return [...codes];
    }, [entries, drafts]);
}

/**
 * The OTHER entries this row's ArbZG warnings were worked out from — the rest
 * of its workday across every project, and, for a short rest, the adjacent day.
 * The row carrying the flag is often not the one to correct. Empty when the
 * entry stands alone.
 */
export function useTimeEntryWarningRelated(
    entryId: string
): TimeEntryDocType[] {
    const entriesQuery = useStableQuery<TimeEntryDocType>({});
    const { results: entriesDocs } = useLiveRxQuery<TimeEntryDocType>({
        collection: 'timeEntries',
        query: entriesQuery
    });
    const entries = useMemo(
        () => entriesDocs.map((doc) => doc.toMutableJSON() as TimeEntryDocType),
        [entriesDocs]
    );
    return useMemo(() => {
        const details = computeTimeEntryWarningDetails(entries).get(entryId);
        if (!details) {
            return [];
        }
        const related = new Set<string>();
        for (const detail of details) {
            for (const id of detail.entryIds) {
                related.add(id);
            }
        }
        // The row itself is in there; the panel lists it separately.
        related.delete(entryId);
        return entries.filter((entry) => related.has(entry.id));
    }, [entries, entryId]);
}

/** The editable fields an ArbZG warning can point at. */
export type TimeEntryWarningField = 'startTime' | 'endTime' | 'pauseMinutes';

/**
 * Which input each warning is about, so the form can mark the fields to change.
 * The break rules point at the BREAK, not the times: the day may be the right
 * length and only the break too short. `longDay` points at both, since net time
 * is span minus break.
 */
const WARNING_FIELDS: Record<TimeEntryWarningCode, TimeEntryWarningField[]> = {
    longDay: ['startTime', 'endTime', 'pauseMinutes'],
    shortBreak30: ['pauseMinutes'],
    shortBreak45: ['pauseMinutes'],
    // § 5 is about the gap to the ADJACENT day; this entry's start and end are
    // one half of it, the other sits on a different day's row.
    shortRest: ['startTime', 'endTime']
};

export function timeEntryWarningFields(
    codes: readonly TimeEntryWarningCode[]
): Set<TimeEntryWarningField> {
    const fields = new Set<TimeEntryWarningField>();
    for (const code of codes) {
        for (const field of WARNING_FIELDS[code] ?? []) {
            fields.add(field);
        }
    }
    return fields;
}

/**
 * The row's status class: a double-booking (`row-conflict`, error) outranks a
 * legal warning (`row-warning`); neither → ''. Pair with `timeEntryRowSx`.
 */
export function timeEntryRowClass(
    conflict: boolean,
    codes: TimeEntryWarningCode[] | undefined
): string {
    if (conflict) {
        return 'row-conflict';
    }
    if (codes && codes.length > 0) {
        return 'row-warning';
    }
    return '';
}

/** Row tints for the conflict/warning classes — spread into a grid's `sx`. */
export const timeEntryRowSx = {
    '& .row-conflict': { backgroundColor: 'rgba(211, 47, 47, 0.10)' },
    '& .row-warning': { backgroundColor: 'rgba(237, 108, 2, 0.10)' }
};
